import name from './name'
import { createSlice } from '@reduxjs/toolkit'

/*
 * `requestStatus` is the one answer to "where is the status request":
 *
 *   idle     nobody has asked yet
 *   loading  the first request is out
 *   ready    it came back; `status` and `referral` are the backend's
 *   error    it failed; `status` and `referral` keep what the last success
 *            brought, or the hidden default when nothing ever came back
 *
 * Asking again once answered (the Home and Account settings each ask when
 * they load) leaves it at ready or error, so what is on screen stays put
 * while the refresh is out.
 */
export const REFERRAL_REQUEST_STATUS = Object.freeze({
  idle: 'idle',
  loading: 'loading',
  ready: 'ready',
  error: 'error'
})

/*
 * The subscriber's place in the referral programme, from the
 * `subscriptionReferralCode` event. Shared because the Home banner and the
 * Account settings section both show it, and the settings drawer needs the
 * status before the section renders.
 *
 * `status` is the backend's: hidden (not eligible), available (eligible, no
 * code yet), active (has a code) or maxed (reached the yearly limit).
 */
const initialState = {
  requestStatus: REFERRAL_REQUEST_STATUS.idle,
  status: 'hidden',
  // The code, its share details and limits, whenever the payload has a code
  referral: null
}

const referral = createSlice({
  name,
  initialState,
  reducers: {
    referralRequested: (state) => {
      if (state.requestStatus === REFERRAL_REQUEST_STATUS.idle) {
        state.requestStatus = REFERRAL_REQUEST_STATUS.loading
      }
    },
    referralReceived: (state, { payload }) => {
      state.requestStatus = REFERRAL_REQUEST_STATUS.ready
      state.status = payload?.status || 'hidden'
      state.referral = payload?.code ? payload : null
    },
    // A first failure fails closed (no banner, no settings entry); a failed
    // refresh keeps the code the subscriber has already seen
    referralFailed: (state) => {
      state.requestStatus = REFERRAL_REQUEST_STATUS.error
    }
  }
})

export const { referralRequested, referralReceived, referralFailed } = referral.actions

export default referral.reducer
